import { useQuery, useMutation } from "@tanstack/react-query";
import { publicHttp, privateHttp, querystring } from "../../config/axios";
import { API_ENDPOINTS } from "../../config/endpoints";
import { setTokens } from "%/token";
import {
  LoginResponse,
  BusinessLoginSelectListResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  UserCreatePetFormResponse,
} from "../../types/common";
import { removeNullInObject } from "%/dataTransform";

export interface SignInRequest {
  snsId: string;
  cdSnsType: string;
}

export interface TestLoginRequest {
  id: string;
  pwd: string;
}

export interface AppLoginRequest {
  cdSnsType: string | null;
  hash: string;
  fcmToken: string | null;
  cdMobileType: string;
}
export interface AppLoginAutoRequest {
  hash: string;
  // hash 만으로 자동로그인할 때도 현재 기기의 FCM 토큰을 서버에 갱신한다(없으면 생략).
  fcmToken?: string | null;
  cdMobileType?: string | null;
}

interface SignInResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: "USER" | "ADMIN";
  };
}

interface SignUpRequest {
  email: string;
  password: string;
  name: string;
  phone: string;
  agree1: boolean;
  agree2: boolean;
}

interface BusinessSignUpRequest {
  email: string;
  name: string;
  phone: string;
  agree1: boolean;
  agree2: boolean;
  snsId: string;
  snsType: string;
}

// 파일 업로드를 위한 인터페이스
interface BusinessSignUpWithFileRequest {
  email: string;
  name: string;
  phone: string;
  agree1: boolean;
  allAlamYn: string;
  snsId: string;
  snsType: string;
  storeName: string;
  storeNumber: string;
  businessLicense?: File; // 웹: 파일 직접 첨부
  businessFileSeq?: number; // 앱: 네이티브 브릿지로 미리 업로드한 사본 fileSeq
  agree3: boolean;
  storeTelNumber: string;
  storeAddress: string;
  storeAddressDetail: string;
  mainBank: string;
  playgroundYn: string;
  mainAccountNumber: string;
  mainAccountName: string; // 예금주(입력값). depositor 로 서버 전송
  // weightClasses: string[];
  facilities: string[];
  serviceProvides: string[];
  longitude: string;
  latitude: string;
  salesPartners: string;
  maxWeight: number;
}

// 비즈니스 강사 회원가입을 위한 인터페이스
interface BusinessTeacherSignUpRequest {
  email: string;
  name: string;
  phone: string;
  agree1: boolean;
  agree2: boolean;
  snsId: string;
  snsType: string;
}

// 사용자(고객) 회원가입 요청 타입
interface UserSignUpRequest {
  email: string;
  userName: string;
  phoneNumber: string;
  termsYn: string;
  agree2: string;
  snsId: string;
  cdSnsType: string;
  nickname: string;
}

// 사용자(고객) 펫 생성 요청 타입
interface UserPetCreateRequest {
  petImg?: string | File;
  fileSeq?: number;
  petName: string;
  petRegNumber: string;
  petType: string;
  gender: string;
  year: string;
  month: string;
  day: string;
  petWeight: string;
  neuterYn: string;
  inoculations:
    | Array<{
        activeYn: string;
        cdInoculation: string;
        inoculationName: string;
      }>
    | undefined;
  personalities:
    | Array<{
        activeYn: string;
        cdPersonality: string;
        personalityName: string;
      }>
    | undefined;
  allergy: string;
  memo: string;
  userName: string;
  userSeq: number;
  mainPetYn: string;
}

interface SignUpResponse {
  statusCode: number;
  data: {
    bizUserSeq: number;
    companySeq: number;
    jwtToken: {
      grantType: string;
      accessToken: string;
      refreshToken: string;
    };
  };
  message: string;
}
interface SignUpResponseUser {
  statusCode: number;
  data: {
    userSeq: number;
    jwtToken: {
      grantType: string;
      accessToken: string;
      refreshToken: string;
      userSeq?: number;
    };
  };
  message: string;
}

export const AUTH_QUERY_KEYS = {
  signIn: ["signIn"],
  signUp: ["signUp"],
} as const;

// 로그인
export const signIn = async (
  request: SignInRequest,
): Promise<SignInResponse> => {
  const { data } = await publicHttp.post<SignInResponse>(
    API_ENDPOINTS.AUTH.SIGN_IN,
    request,
  );
  return data;
};

// 회원가입
export const signUp = async (
  request: SignUpRequest,
): Promise<SignUpResponse> => {
  const { data } = await publicHttp.post<SignUpResponse>(
    API_ENDPOINTS.AUTH.SIGN_UP,
    request,
  );
  return data;
};

// 비즈니스 회원가입 (파일 포함)
export const businessSignUpWithFile = async (
  request: BusinessSignUpWithFileRequest,
): Promise<SignUpResponse> => {
  const formData = new FormData();

  // 새로운 API 형식에 맞는 데이터 구조 생성
  const requestData = {
    email: request.email || "",
    snsId: request.snsId || "",
    cdSnsType: request.snsType,
    bizUserName: request.name || "",
    phoneNumber: request.phone.replace(/-/g, "") || "",
    cdUserType: "USER_TYPE_OWNER", // 비즈니스 사용자 타입
    agree1: request.agree1,
    allAlamYn: request.allAlamYn,
    agree3: request.agree3,
    salesPartners: request.salesPartners || "",
    companyInfo: {
      // weightClasses: request.weightClasses,
      // 예금주 = 입력한 mainAccountName 우선(없으면 대표자명 폴백)
      depositor: request.mainAccountName || request.name || "",
      facilities: request.facilities,
      companyName: request.storeName || "",
      // endTime: '18:00:00',
      businessNumber: request.storeNumber.replace(/-/g, "") || "",
      mainTelNumber: request.storeTelNumber.replace(/-/g, "") || "",
      // latitude: '37.5665', // 서울 기본값
      serviceProvides: request.serviceProvides,
      // zipcode: '12345',
      companyAddr: request.storeAddress || "",
      longitude: request.longitude || "",
      latitude: request.latitude || "",
      // startTime: '09:00:00',
      playgroundYn: request.playgroundYn,
      mainBank: request.mainBank || "",
      mainAccountNumber: request.mainAccountNumber || "",
      salesManagerSeq: request.salesPartners
        ? [parseInt(request.salesPartners)]
        : [],
      companyAddrDetail: request.storeAddressDetail || "",
      maxWeight: request.maxWeight,
      // 앱 네이티브 브릿지로 사본을 먼저 올린 경우 그 fileSeq 를 함께 전달(파일 재업로드 없음)
      businessFileSeq: request.businessFileSeq,
    },
  };
  // console.log(requestData);
  // JSON 데이터를 FormData에 추가(문자열 → 파트 CT 미지정, boundary 정상이면 서버가 정상 파싱)
  formData.append("param", JSON.stringify(requestData));

  // 파일 추가 — 앱(fileSeq) 경로에서는 파일을 다시 올리지 않는다
  if (request.businessLicense && !request.businessFileSeq) {
    formData.append("file", request.businessLicense);
  }

  try {
    const { data } = await publicHttp.post<SignUpResponse>(
      API_ENDPOINTS.AUTH_BUSINESS.SIGN_UP,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    console.log(data);
    // 회원가입 성공 시 토큰 저장
    if (
      data.statusCode === 200 &&
      data.data.jwtToken?.accessToken &&
      data.data.jwtToken?.refreshToken
    ) {
      console.log(
        data.data.jwtToken.accessToken,
        data.data.jwtToken.refreshToken,
      );
      setTokens(
        data.data.jwtToken.accessToken,
        data.data.jwtToken.refreshToken,
      );
    }

    return data;
  } catch (error) {
    console.error(
      "Business SignUp With File Error:",
      JSON.stringify({
        error: error,
        requestData: requestData,
        hasFile: !!request.businessLicense,
      }),
    );
    throw error;
  }
};

// 비즈니스 회원가입 (기존 - 파일 없음)
export const businessSignUp = async (
  request: BusinessSignUpRequest,
): Promise<SignUpResponse> => {
  const { data } = await publicHttp.post<SignUpResponse>(
    API_ENDPOINTS.AUTH_BUSINESS.SIGN_UP,
    request,
  );

  // 회원가입 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data.jwtToken?.accessToken &&
    data.data.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

// 로그아웃
export const signOut = async (): Promise<void> => {
  await privateHttp.post(API_ENDPOINTS.AUTH.SIGN_OUT);
};

// 사업자등록번호 사전 검증 (형식·체크섬 + 중복). 가입 폼 '다음' 전 호출.
export interface BusinessNumberCheckResult {
  valid: boolean;
  available: boolean;
  maskedEmail?: string | null;
  snsType?: string | null;
  message?: string | null;
}
export const checkBusinessNumber = async (
  businessNumber: string,
): Promise<BusinessNumberCheckResult> => {
  const { data } = await publicHttp.get<{ data: BusinessNumberCheckResult }>(
    API_ENDPOINTS.AUTH_BUSINESS.CHECK_BUSINESS_NUMBER,
    querystring({ businessNumber }),
  );
  return (
    data?.data ?? { valid: false, available: false, message: "검증에 실패했습니다." }
  );
};

// 토큰 갱신
export const refreshToken = async (
  refreshToken: string,
): Promise<{ accessToken: string }> => {
  const { data } = await publicHttp.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
    refreshToken,
  });
  return data;
};

// 비즈니스 토큰 갱신
export const businessRefreshToken = async (
  request: RefreshTokenRequest,
): Promise<RefreshTokenResponse> => {
  try {
    console.log("Calling refresh token API with:", {
      refreshToken: request.refreshToken?.substring(0, 10) + "...",
    });

    const { data } = await publicHttp.get<RefreshTokenResponse>(
      API_ENDPOINTS.AUTH_BUSINESS.REFRESH_TOKEN,
      {
        headers: {
          "X-Refresh-Token": request.refreshToken,
        },
      },
    );

    console.log("Refresh token API response:", {
      statusCode: data.statusCode,
      hasAccessToken: !!data.data?.accessToken,
      hasRefreshToken: !!data.data?.refreshToken,
    });

    // axios 인터셉터에서 토큰 저장을 처리하므로 여기서는 저장하지 않음
    return data;
  } catch (error) {
    console.error("Business refresh token API call failed:", error);
    throw error;
  }
};

export const businessLogin = async (
  request: SignInRequest,
): Promise<LoginResponse> => {
  const query = removeNullInObject({
    snsId: request.snsId,
    cdSnsType: request.cdSnsType,
  });
  const { data } = await publicHttp.get<LoginResponse>(
    API_ENDPOINTS.AUTH_BUSINESS.LOG_IN,
    querystring(query),
  );
  console.log(data);

  // 로그인 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data?.jwtToken?.accessToken &&
    data.data?.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

// 비즈니스 테스트 로그인 (GET 방식)
export const businessTestLogin = async (
  request: TestLoginRequest,
): Promise<LoginResponse> => {
  const query = removeNullInObject({
    id: request.id,
    pwd: request.pwd,
  });
  const { data } = await publicHttp.get<LoginResponse>(
    API_ENDPOINTS.AUTH_BUSINESS.LOG_IN_TEST,
    querystring(query),
  );
  console.log("Business test login response:", data);

  // 로그인 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data?.jwtToken?.accessToken &&
    data.data?.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

export const useBusinessLogin = (
  request: SignInRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...AUTH_QUERY_KEYS.signIn, request],
    queryFn: () => businessLogin(request),
    enabled: enabled && !!request.snsId,
  });
};

export const useBusinessTestLogin = (
  request: TestLoginRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...AUTH_QUERY_KEYS.signIn, "test", request],
    queryFn: () => businessTestLogin(request),
    enabled: enabled && !!request.id,
  });
};

// 비즈니스 앱 로그인 (GET 방식)
export const businessAppLogin = async (
  request: AppLoginRequest,
): Promise<LoginResponse> => {
  // alert("사업자자 로그인 전");
  const query = removeNullInObject({
    cdSnsType: request.cdSnsType,
    hash: request.hash,
    fcmToken: request.fcmToken,
    cdMobileType: request.cdMobileType,
  });
  // alert(JSON.stringify(query));
  const { data } = await publicHttp.get<LoginResponse>(
    API_ENDPOINTS.AUTH_BUSINESS.APP_LOG_IN,
    querystring(query),
  );
  // alert("사업자 로그인 후");
  // alert(JSON.stringify(data));
  // console.log(data);

  // 로그인 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data?.jwtToken?.accessToken &&
    data.data?.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

// 비즈니스 앱 로그인 (GET 방식)
export const businessAppLoginAuto = async (
  request: AppLoginAutoRequest,
): Promise<LoginResponse> => {
  // alert("사업자자 로그인 전");
  const query = removeNullInObject({
    hash: request.hash,
    fcmToken: request.fcmToken ?? null,
    cdMobileType: request.cdMobileType ?? null,
  });
  // alert(JSON.stringify(query));
  const { data } = await publicHttp.get<LoginResponse>(
    API_ENDPOINTS.AUTH_BUSINESS.APP_LOG_IN_AUTO,
    querystring(query),
  );
  // alert("사업자 로그인 후");
  // alert(JSON.stringify(data));
  // console.log(data);

  // 로그인 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data?.jwtToken?.accessToken &&
    data.data?.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

export const useBusinessAppLogin = (
  request: AppLoginRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...AUTH_QUERY_KEYS.signIn, "app", request],
    queryFn: () => businessAppLogin(request),
    enabled:
      enabled && !!request.cdSnsType && !!request.hash && !!request.fcmToken,
  });
};

// 사용자(고객) 앱 로그인 (GET 방식)
export const userAppLogin = async (
  request: AppLoginRequest,
): Promise<LoginResponse> => {
  const query = removeNullInObject({
    cdSnsType: request.cdSnsType,
    hash: request.hash,
    fcmToken: request.fcmToken,
    cdMobileType: request.cdMobileType,
  });
  // alert("사용자");
  console.log("사용자 로그인 전", JSON.stringify(request));
  const { data } = await publicHttp.get<LoginResponse>(
    API_ENDPOINTS.AUTH_CUSTOMER.APP_LOG_IN,
    querystring(query),
  );
  // alert("사용자 로그인 후");
  // alert(JSON.stringify(data));
  console.log("사용자 로그인 후", JSON.stringify(data));

  // 로그인 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data?.jwtToken?.accessToken &&
    data.data?.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

export const userAppLoginAuto = async (
  request: AppLoginAutoRequest,
): Promise<LoginResponse> => {
  const query = removeNullInObject({
    hash: request.hash,
    fcmToken: request.fcmToken ?? null,
    cdMobileType: request.cdMobileType ?? null,
  });
  // alert("사용자");
  const { data } = await publicHttp.get<LoginResponse>(
    API_ENDPOINTS.AUTH_CUSTOMER.APP_LOG_IN_AUTO,
    querystring(query),
  );
  // alert("사용자 로그인 후");
  // alert(JSON.stringify(data));
  console.log(data);

  // 로그인 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data?.jwtToken?.accessToken &&
    data.data?.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

export const useUserAppLogin = (
  request: AppLoginRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...AUTH_QUERY_KEYS.signIn, "user", "app", request],
    queryFn: () => userAppLogin(request),
    enabled:
      enabled && !!request.cdSnsType && !!request.hash && !!request.fcmToken,
  });
};

export const useBusinessSignUp = () => {
  return useMutation({
    mutationKey: [...AUTH_QUERY_KEYS.signUp],
    mutationFn: businessSignUp,
  });
};

// 비즈니스 강사 회원가입 (새로운 API 형식)
export const businessTeacherSignUp = async (
  request: BusinessTeacherSignUpRequest,
): Promise<SignUpResponse> => {
  const formData = new FormData();

  // 새로운 API 형식에 맞는 데이터 구조 생성
  const requestData = {
    email: request.email || "",
    snsId: request.snsId || "",
    cdSnsType: request.snsType, // 카카오 기본값
    bizUserName: request.name || "",
    phoneNumber: request.phone.replace(/-/g, "") || "",
    cdUserType: "USER_TYPE_TEACHER", // 강사 사용자 타입
  };

  // JSON 데이터를 FormData에 추가
  formData.append("param", JSON.stringify(requestData));

  // 파일이 없어도 FormData로 전송
  const { data } = await publicHttp.post<SignUpResponse>(
    API_ENDPOINTS.AUTH_BUSINESS.SIGN_UP,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  // 회원가입 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data.jwtToken?.accessToken &&
    data.data.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

export const useBusinessSignUpWithFile = () => {
  return useMutation({
    mutationKey: [...AUTH_QUERY_KEYS.signUp, "withFile"],
    mutationFn: businessSignUpWithFile,
  });
};

export const useBusinessTeacherSignUp = () => {
  return useMutation({
    mutationKey: [...AUTH_QUERY_KEYS.signUp, "teacher"],
    mutationFn: businessTeacherSignUp,
  });
};

export const businessLoginSelectList =
  async (): Promise<BusinessLoginSelectListResponse> => {
    const { data } = await publicHttp.get<{
      data: BusinessLoginSelectListResponse;
    }>(`${API_ENDPOINTS.AUTH_BUSINESS.SIGN_IN_SELECTLIST}`);
    return data.data;
  };

export const useBusinessLoginSelectList = () => {
  return useQuery({
    queryKey: [...AUTH_QUERY_KEYS.signIn, "selectList"],
    queryFn: businessLoginSelectList,
  });
};

// 사용자 로그인 (GET 방식 - business login과 동일 패턴)
export const userLogin = async (
  request: SignInRequest,
): Promise<LoginResponse> => {
  const query = removeNullInObject({
    snsId: request.snsId,
    cdSnsType: request.cdSnsType,
  });
  const { data } = await publicHttp.get<LoginResponse>(
    API_ENDPOINTS.AUTH_CUSTOMER.LOG_IN,
    querystring(query),
  );
  console.log(data);

  // 로그인 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data?.jwtToken?.accessToken &&
    data.data?.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

// 사용자 테스트 로그인 (GET 방식)
export const userTestLogin = async (
  request: TestLoginRequest,
): Promise<LoginResponse> => {
  const query = removeNullInObject({
    id: request.id,
    pwd: request.pwd,
  });
  const { data } = await publicHttp.get<LoginResponse>(
    API_ENDPOINTS.AUTH_CUSTOMER.LOG_IN_TEST,
    querystring(query),
  );
  console.log("User test login response:", data);

  // 로그인 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data?.jwtToken?.accessToken &&
    data.data?.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

export const useUserLogin = (
  request: SignInRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...AUTH_QUERY_KEYS.signIn, "user", request],
    queryFn: () => userLogin(request),
    enabled: enabled && !!request.snsId,
  });
};

export const useUserTestLogin = (
  request: TestLoginRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...AUTH_QUERY_KEYS.signIn, "user", "test", request],
    queryFn: () => userTestLogin(request),
    enabled: enabled && !!request.id,
  });
};

// 사용자 토큰 갱신
export const userRefreshToken = async (
  request: RefreshTokenRequest,
): Promise<RefreshTokenResponse> => {
  try {
    console.log("Calling user refresh token API with:", {
      refreshToken: request.refreshToken?.substring(0, 10) + "...",
    });

    const { data } = await publicHttp.get<RefreshTokenResponse>(
      API_ENDPOINTS.AUTH_CUSTOMER.REFRESH_TOKEN,
      {
        headers: {
          "X-Refresh-Token": request.refreshToken,
        },
      },
    );

    console.log("User refresh token API response:", {
      statusCode: data.statusCode,
      hasAccessToken: !!data.data?.accessToken,
      hasRefreshToken: !!data.data?.refreshToken,
    });

    return data;
  } catch (error) {
    console.error("User refresh token API call failed:", error);
    throw error;
  }
};

// 사용자 로그인 폼 선택 목록 조회
export const userLoginSelectList =
  async (): Promise<BusinessLoginSelectListResponse> => {
    const { data } = await publicHttp.get<{
      data: BusinessLoginSelectListResponse;
    }>(`${API_ENDPOINTS.AUTH_CUSTOMER.SIGN_IN_SELECTLIST}`);
    return data.data;
  };

export const useUserLoginSelectList = () => {
  return useQuery({
    queryKey: [...AUTH_QUERY_KEYS.signIn, "user", "selectList"],
    queryFn: userLoginSelectList,
  });
};

// 사용자(고객) 펫 생성 폼 데이터 조회
export const userCreatePetForm =
  async (): Promise<UserCreatePetFormResponse> => {
    const { data } = await publicHttp.get<{
      data: UserCreatePetFormResponse;
    }>(API_ENDPOINTS.AUTH_CUSTOMER.CREATE_PET_FORM);
    return data.data;
  };

export const useUserCreatePetForm = () => {
  return useQuery({
    queryKey: [...AUTH_QUERY_KEYS.signIn, "user", "createPetForm"],
    queryFn: userCreatePetForm,
    staleTime: 0, // 즉시 stale로 처리하여 항상 새로운 데이터 가져오기
    gcTime: 0, // 캐시 시간을 0으로 설정
    refetchOnWindowFocus: true, // 윈도우 포커스 시에도 재호출
  });
};

// 사용자(고객) 회원가입 (기본)
export const userSignUp = async (
  request: UserSignUpRequest,
): Promise<SignUpResponseUser> => {
  // 광고성 정보 수신 동의 여부에 따라 알림 설정
  const alarmYn = request.agree2 === "Y" ? "Y" : "N";

  const { data } = await publicHttp.post<SignUpResponseUser>(
    API_ENDPOINTS.AUTH_CUSTOMER.SIGN_UP,
    {
      cdUserType: "USER_TYPE_PET_OWNER",
      email: request.email || "",
      snsId: request.snsId || "",
      cdSnsType: request.cdSnsType || "",
      userName: request.userName || "",
      nickname: request.nickname || "",
      phoneNumber: request.phoneNumber.replace(/-/g, "") || "",
      termsYn: request.termsYn,
      allAlarmYn: alarmYn,
      alarmNoticeYn: alarmYn,
      alarmMarketingYn: alarmYn,
      alarmSmsYn: alarmYn,
      alarmEmailYn: alarmYn,
    },
  );
  // 회원가입 성공 시 토큰 저장
  if (
    data.statusCode === 200 &&
    data.data?.jwtToken?.accessToken &&
    data.data?.jwtToken?.refreshToken
  ) {
    setTokens(data.data.jwtToken.accessToken, data.data.jwtToken.refreshToken);
  }

  return data;
};

// React Query 훅
export const useUserSignUp = () => {
  return useMutation({
    mutationKey: [...AUTH_QUERY_KEYS.signUp, "user"],
    mutationFn: userSignUp,
  });
};

// 사용자(고객) 펫 생성 (multipart/form-data)
export const userPetCreate = async (
  request: UserPetCreateRequest,
): Promise<SignUpResponse> => {
  const formData = new FormData();

  // 생년월일 포맷 변경 (YYYYMMDD)
  const petBirthday = `${request.year}${request.month.padStart(2, "0")}${request.day.padStart(2, "0")}`;

  // 새로운 API 형식에 맞는 데이터 구조 생성
  const requestData = {
    allergy: request.allergy || "",
    fileSeq: request.fileSeq || 0, // 네이티브 브릿지로 미리 업로드된 파일 SEQ (없으면 0, file 파트로 처리)
    petName: request.petName || "",
    personalities: request.personalities,
    inoculations: request.inoculations,
    userSeq: request.userSeq, // 서버에서 토큰으로 처리
    cdPetBreed: request.petType,
    petWeight: parseInt(request.petWeight) || 0,
    neuterYn: request.neuterYn,
    memo: request.memo || "",
    petBirthday: petBirthday,
    petRegNumber: request.petRegNumber || "",
    cdPetGender: request.gender,
  };

  console.log("Pet create request data:", requestData);

  // JSON 데이터를 FormData에 추가
  formData.append("param", JSON.stringify(requestData));

  // 파일 추가
  if (request.petImg) {
    if (request.petImg instanceof File) {
      formData.append("file", request.petImg);
    } else if (
      typeof request.petImg === "string" &&
      request.petImg.startsWith("data:")
    ) {
      // Base64 문자열을 Blob으로 변환
      const response = await fetch(request.petImg);
      const blob = await response.blob();
      formData.append("file", blob, "pet-image.jpg");
    }
  }

  const { data } = await privateHttp.post<SignUpResponse>(
    API_ENDPOINTS.AUTH_CUSTOMER.PET_CREATE,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return data;
};

export const useUserPetCreate = () => {
  return useMutation({
    mutationKey: [...AUTH_QUERY_KEYS.signUp, "user", "petCreate"],
    mutationFn: userPetCreate,
  });
};

// 사용자(고객) 펫 업데이트 (multipart/form-data)
export const userPetUpdate = async (
  request: UserPetCreateRequest & { petSeq: number },
): Promise<SignUpResponse> => {
  const formData = new FormData();

  // 생년월일 포맷 변경 (YYYYMMDD)
  const petBirthday = `${request.year}${request.month.padStart(2, "0")}${request.day.padStart(2, "0")}`;

  // 새로운 API 형식에 맞는 데이터 구조 생성
  const requestData = {
    petSeq: request.petSeq,
    allergy: request.allergy || "",
    fileSeq: request.fileSeq || 0, // 네이티브 브릿지로 미리 업로드된 파일 SEQ (없으면 0, file 파트로 처리)
    petName: request.petName || "",
    personalities: request.personalities,
    inoculations: request.inoculations,
    userSeq: request.userSeq, // 서버에서 토큰으로 처리
    cdPetBreed: request.petType,
    petWeight: parseInt(request.petWeight) || 0,
    neuterYn: request.neuterYn,
    memo: request.memo || "",
    petBirthday: petBirthday,
    petRegNumber: request.petRegNumber || "",
    cdPetGender: request.gender,
    mainPetYn: request.mainPetYn,
  };

  console.log("Pet update request data:", requestData);

  // JSON 데이터를 FormData에 추가
  formData.append("param", JSON.stringify(requestData));

  // 파일 추가
  console.log("=== 파일 처리 시작 ===");
  console.log("request.petImg:", request.petImg);
  console.log("request.petImg type:", typeof request.petImg);
  console.log(
    "request.petImg instanceof File:",
    request.petImg instanceof File,
  );

  if (request.petImg && request.petImg !== "") {
    if (request.petImg instanceof File) {
      console.log("File 객체로 처리");
      formData.append("file", request.petImg);
    } else if (
      typeof request.petImg === "string" &&
      request.petImg.startsWith("data:")
    ) {
      console.log("Base64 data URL로 처리");
      // Base64 문자열을 Blob으로 변환
      const response = await fetch(request.petImg);
      const blob = await response.blob();
      formData.append("file", blob, "pet-image.jpg");
    } else if (
      typeof request.petImg === "string" &&
      (request.petImg.startsWith("http") || request.petImg.startsWith("/"))
    ) {
      console.log("기존 URL이므로 파일 업로드 생략");
      // 기존 이미지 URL이므로 파일 업로드 생략
      formData.append("file", "");
    } else {
      console.log("알 수 없는 이미지 형식");
      formData.append("file", "");
    }
  } else {
    console.log("이미지가 없거나 변경되지 않음");
    formData.append("file", "");
  }

  console.log("=== 파일 처리 완료 ===");

  const { data } = await privateHttp.post<SignUpResponse>(
    API_ENDPOINTS.USER_MYPAGE.PET_UPDATE,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return data;
};

export const useUserPetUpdate = () => {
  return useMutation({
    mutationKey: [...AUTH_QUERY_KEYS.signUp, "user", "petUpdate"],
    mutationFn: userPetUpdate,
  });
};

// 비즈니스 회원탈퇴 인터페이스
interface BusinessWithdrawalResponse {
  statusCode: number;
  message: string;
}

// 사용자 회원탈퇴 인터페이스
interface UserWithdrawalResponse {
  statusCode: number;
  message: string;
}

// 비즈니스 회원탈퇴 API
export const businessWithdrawal =
  async (): Promise<BusinessWithdrawalResponse> => {
    const { data } = await privateHttp.patch<BusinessWithdrawalResponse>(
      API_ENDPOINTS.AUTH_BUSINESS.WITHDRAWAL,
    );
    return data;
  };

// 사용자 회원탈퇴 API
export const userWithdrawal = async (): Promise<UserWithdrawalResponse> => {
  const { data } = await privateHttp.patch<UserWithdrawalResponse>(
    API_ENDPOINTS.AUTH_CUSTOMER.WITHDRAWAL,
  );
  return data;
};

// 비즈니스 회원탈퇴 훅
export const useBusinessWithdrawal = () => {
  return useMutation({
    mutationKey: [...AUTH_QUERY_KEYS.signUp, "business", "withdrawal"],
    mutationFn: businessWithdrawal,
  });
};

// 사용자 회원탈퇴 훅
export const useUserWithdrawal = () => {
  return useMutation({
    mutationKey: [...AUTH_QUERY_KEYS.signUp, "user", "withdrawal"],
    mutationFn: userWithdrawal,
  });
};
